import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams, useSearchParams } from "react-router"
import Select from "react-select"
import DatePicker from "react-datepicker"

import { httpOk, GET, POST, PUT } from "helpers/api"
import { formatDate } from "helpers/helpers"

function defaultItem() {
	return {
		name: "",
		comment: "",
		start_date: formatDate(new Date(), "yyyy-MM-dd"),
		end_date: "",
		/** attendance_hours | shift_hours | attendance_days | schedule_days | schedule_hours */
		rate_basis: "attendance_hours",
		/** by_position | by_user */
		participant_mode: "by_position",
		rate: "",
		/** порог чеков за день для sales_days; пусто = 3 по умолчанию */
		min_cheques_per_day: "",
		is_active: true,
		/** пусто = тариф действует на всех торговых точках организации */
		pos_ids: [],
		user_position_ids: [],
		user_ids: [],
	}
}

function rateBasisOptions(t) {
	return [
		{ value: "attendance_hours", label: t("rate_attendance_hours") },
		{ value: "shift_hours", label: t("rate_shift_hours") },
		{ value: "attendance_days", label: t("rate_attendance_days") },
		{ value: "schedule_days", label: t("rate_schedule_days") },
		{ value: "schedule_hours", label: t("rate_schedule_hours") },
		{ value: "sales_days", label: t("rate_sales_days") },
	]
}

/** Единица, в которой задаётся ставка выбранного основания */
function rateUnitLabel(t, basis) {
	if (basis === "attendance_days" || basis === "sales_days") return t("days")
	if (basis === "schedule_days") return t("shift")
	return t("hourly")
}

function toPositiveIntArray(raw) {
	const list = Array.isArray(raw) ? raw : []
	return list
		.map((x) => Number(typeof x === "object" && x !== null && x.id != null ? x.id : x))
		.filter((n) => !Number.isNaN(n) && n > 0)
}

function CreateUpdate() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const copyId = searchParams.get("copy")

	const [data, setData] = useState(defaultItem())
	const [poses, setPoses] = useState([])
	const [usersCatalog, setUsersCatalog] = useState([])
	const [usersPositions, setUsersPositions] = useState([])
	const [positionSearch, setPositionSearch] = useState("")
	const [userSearch, setUserSearch] = useState("")

	const basisOptions = useMemo(() => rateBasisOptions(t), [t])

	const filteredPositions = usersPositions.filter((p) =>
		String(p?.name ?? "").toLowerCase().includes(positionSearch.toLowerCase())
	)
	const filteredUsers = usersCatalog.filter((u) =>
		String(u?.name ?? "").toLowerCase().includes(userSearch.toLowerCase())
	)

	function positionIdSelected(positionId) {
		return (data.user_position_ids || []).some((x) => Number(x) === Number(positionId))
	}

	function togglePosition(positionId) {
		const numberId = Number(positionId)
		const ids = toPositiveIntArray(data.user_position_ids)
		const index = ids.indexOf(numberId)
		if (index >= 0) ids.splice(index, 1)
		else ids.push(numberId)
		setData({ ...data, user_position_ids: ids })
	}

	function userIdSelected(userId) {
		return (data.user_ids || []).some((x) => Number(x) === Number(userId))
	}

	function toggleUser(userId) {
		const numberId = Number(userId)
		const ids = toPositiveIntArray(data.user_ids)
		const index = ids.indexOf(numberId)
		if (index >= 0) ids.splice(index, 1)
		else ids.push(numberId)
		setData({ ...data, user_ids: ids })
	}

	async function createUpdate(e) {
		e.preventDefault()

		const payload = {
			name: data.name,
			comment: data.comment || null,
			start_date: data.start_date || formatDate(new Date(), "yyyy-MM-dd"),
			end_date: data.end_date || null,
			rate_basis: data.rate_basis,
			participant_mode: data.participant_mode,
			rate: data.rate === "" || data.rate === null ? 0 : Number(data.rate),
			min_cheques_per_day: data.rate_basis === "sales_days" && data.min_cheques_per_day
				? Number(data.min_cheques_per_day)
				: null,
			is_active: !!data.is_active,
			pos_ids: toPositiveIntArray(data.pos_ids),
			user_position_ids: data.participant_mode === "by_position"
				? toPositiveIntArray(data.user_position_ids)
				: [],
			user_ids: data.participant_mode === "by_user"
				? toPositiveIntArray(data.user_ids)
				: [],
		}

		let response
		if (id) {
			response = await PUT(`/employee-time-rates/${id}`, payload, { loader: true })
		} else {
			response = await POST("/employee-time-rates", payload, { loader: true })
		}

		if (httpOk(response)) navigate("/employee-time-rates")
	}

	/**
	 * Загрузка тарифа: при ?copy=<id> поля переносятся в новый тариф, но период
	 * не наследуется — новая ставка обычно начинается с новой даты.
	 */
	async function getData() {
		const rateId = id || copyId
		if (!rateId) return

		const response = await GET(`/employee-time-rates/${rateId}`, {}, { loader: true })
		if (httpOk(response)) {
			const row = response.data
			const merged = {
				...defaultItem(),
				...row,
				start_date: id ? formatDate(row.start_date, "yyyy-MM-dd") : formatDate(new Date(), "yyyy-MM-dd"),
				end_date: id && row.end_date ? formatDate(row.end_date, "yyyy-MM-dd") : "",
				rate: row.rate ?? "",
				min_cheques_per_day: row.min_cheques_per_day ?? "",
				is_active: !!row.is_active,
				pos_ids: toPositiveIntArray(row.pos_ids),
				user_position_ids: toPositiveIntArray(row.user_position_ids),
				user_ids: toPositiveIntArray(row.user_ids),
			}
			if (!id) {
				merged.name = `${row.name} (${t("copy")})`
				delete merged.id
			}
			setData(merged)
		}
	}

	async function getPoses() {
		const response = await GET("/helper/poses", {}, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	async function getUsersPositions() {
		const response = await GET("/helper/employee-positions")
		if (httpOk(response)) setUsersPositions(response.data)
	}

	async function getUsers() {
		const response = await GET("/helper/users")
		if (httpOk(response)) setUsersCatalog(response.data)
	}

	useEffect(() => {
		getData()
		getPoses()
		getUsersPositions()
		getUsers()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">{t("time_rates")}</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{id ? t("edit") : t("create")}</h6>
					</div>
				</div>
			</div>

			<div className="card" style={{ 'minHeight': '500px' }}>
				<div className="card-body">
					<form onSubmit={createUpdate} autoComplete="off">
						<div className="row">
							<div className="col-md-6">
								<div className="form-group">
									<label>{t("title")}<span className="required-mark">*</span></label>
									<input
										type="text"
										className="form-control"
										value={data.name || ""}
										onChange={(e) => setData({ ...data, name: e.target.value })}
									/>
								</div>

								<div className="form-group">
									<label>{t("comment")}</label>
									<textarea
										className="form-control"
										rows="3"
										value={data.comment || ""}
										onChange={(e) => setData({ ...data, comment: e.target.value })}
									/>
								</div>

								<div className="form-check mb-3">
									<input
										type="checkbox"
										className="form-check-input"
										id="time-rate-is-active"
										checked={!!data.is_active}
										onChange={(e) => setData({ ...data, is_active: e.target.checked })}
									/>
									<label className="form-check-label" htmlFor="time-rate-is-active">
										{t("active")}
									</label>
								</div>

								<div className="row">
									<div className="form-group col-md-6">
										<label>{t("launch_date")}<span className="required-mark">*</span></label>
										<div className="calendar-input">
											<DatePicker
												showMonthDropdown
												showYearDropdown
												dropdownMode="select"
												yearDropdownItemNumber={2}
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={data.start_date ? new Date(data.start_date) : ''}
												onChange={(date) => setData({ ...data, 'start_date': formatDate(date, 'yyyy-MM-dd') })} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
									<div className="form-group col-md-6">
										<label>{t("stop_date")}</label>
										<div className="calendar-input">
											<DatePicker
												showMonthDropdown
												showYearDropdown
												dropdownMode="select"
												yearDropdownItemNumber={2}
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={data.end_date ? new Date(data.end_date) : ''}
												onChange={(date) => setData({ ...data, 'end_date': date ? formatDate(date, 'yyyy-MM-dd') : '' })} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
								</div>

								<div className="form-group">
									<label>{t("rate_basis")}<span className="required-mark">*</span></label>
									<Select
										options={basisOptions}
										value={basisOptions.find((option) => option.value === data.rate_basis)}
										onChange={(option) => setData({ ...data, rate_basis: option ? option.value : "attendance_hours" })}
										placeholder=""
										noOptionsMessage={() => t("list_empty")}
										getOptionLabel={(option) => option.label}
										getOptionValue={(option) => option.value}
									/>
									{(data.rate_basis === "schedule_days" || data.rate_basis === "schedule_hours") &&
										<span className="text-muted small">{t("rate_schedule_hint")}</span>
									}
									{data.rate_basis === "sales_days" &&
										<span className="text-muted small">{t("rate_sales_days_hint")}</span>
									}
								</div>

								{data.rate_basis === "sales_days" &&
									<div className="form-group">
										<label>{t("min_cheques_per_day")}</label>
										<input
											type="number"
											min="1"
											max="1000"
											className="form-control"
											placeholder="3"
											value={data.min_cheques_per_day ?? ""}
											onChange={(e) => setData({ ...data, min_cheques_per_day: e.target.value })}
										/>
										<span className="text-muted small">{t("min_cheques_per_day_hint")}</span>
									</div>
								}

								<div className="form-group">
									<label>
										{t("rate_amount")}<span className="required-mark">*</span>
										<span className="text-muted ms-1">
											({rateUnitLabel(t, data.rate_basis)})
										</span>
									</label>
									<input
										type="number"
										step="0.01"
										min="0"
										className="form-control"
										value={data.rate === null || data.rate === undefined ? "" : data.rate}
										onChange={(e) => setData({ ...data, rate: e.target.value })}
									/>
								</div>

								<div className="form-group">
									<label>{t("poses")}</label>
									<Select
										isMulti
										isClearable
										options={poses}
										value={poses.filter((p) =>
											(data.pos_ids || []).some((posId) => Number(posId) === Number(p.id))
										)}
										onChange={(options) => setData({
											...data,
											pos_ids: options ? options.map((o) => Number(o.id)) : [],
										})}
										placeholder=""
										noOptionsMessage={() => t("list_empty")}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>

							<div className="col-md-6">
								{id &&
									<div className="alert alert-warning py-2 px-3 small">
										{t("time_rate_history_hint")}
									</div>
								}
								<div className="form-group">
									<label>{t("program_participants")}:</label>
								</div>
								<div className="d-flex gap-2 mb-2">
									<div className="form-check">
										<div className="radio-round"
											onClick={() => setData({ ...data, participant_mode: "by_position" })}
											style={{ 'backgroundColor': data.participant_mode === "by_position" ? "black" : "" }}
										/>
										<span onClick={() => setData({ ...data, participant_mode: "by_position" })}>
											{t("positions")}
										</span>
									</div>
									<div className="form-check">
										<div className="radio-round"
											onClick={() => setData({ ...data, participant_mode: "by_user" })}
											style={{ 'backgroundColor': data.participant_mode === "by_user" ? "black" : "" }}
										/>
										<span onClick={() => setData({ ...data, participant_mode: "by_user" })}>
											{t("employees")}
										</span>
									</div>
								</div>

								{data.participant_mode === "by_position" &&
									<>
										<div className="form-group mb-2">
											<label className="small">{t("search")}:</label>
											<input
												type="text"
												className="form-control form-control-sm"
												value={positionSearch}
												onChange={(e) => setPositionSearch(e.target.value)}
											/>
										</div>
										<div className="border rounded px-2 py-1"
											style={{ maxHeight: "360px", overflowY: "auto" }}>
											{filteredPositions.map((p) => (
												<div key={p.id} className="form-check">
													<input
														className="form-check-input"
														type="checkbox"
														id={`time-rate-position-${p.id}`}
														checked={positionIdSelected(p.id)}
														onChange={() => togglePosition(p.id)}
													/>
													<label className="form-check-label" htmlFor={`time-rate-position-${p.id}`}>
														{p.name}
													</label>
												</div>
											))}
											{!filteredPositions.length &&
												<div className="text-muted small py-1">{t("list_empty")}</div>
											}
										</div>
									</>
								}

								{data.participant_mode === "by_user" &&
									<>
										<div className="form-group mb-2">
											<label className="small">{t("search")}:</label>
											<input
												type="text"
												className="form-control form-control-sm"
												value={userSearch}
												onChange={(e) => setUserSearch(e.target.value)}
											/>
										</div>
										<div className="border rounded px-2 py-1"
											style={{ maxHeight: "360px", overflowY: "auto" }}>
											{filteredUsers.map((u) => (
												<div key={u.id} className="form-check">
													<input
														className="form-check-input"
														type="checkbox"
														id={`time-rate-user-${u.id}`}
														checked={userIdSelected(u.id)}
														onChange={() => toggleUser(u.id)}
													/>
													<label className="form-check-label" htmlFor={`time-rate-user-${u.id}`}>
														{u.name}
													</label>
												</div>
											))}
											{!filteredUsers.length &&
												<div className="text-muted small py-1">{t("list_empty")}</div>
											}
										</div>
									</>
								}
							</div>
						</div>

						<div className="btn-wrapper gap-2 mt-5">
							<button type="button" className="btn btn-warning btn-rounded btn-wide"
								onClick={() => navigate(-1)}>
								{t("cancel")}
							</button>
							<button type="submit" className="btn btn-primary btn-rounded btn-wide">
								{t("save")}
							</button>
						</div>
					</form>
				</div>
			</div>
		</>
	)
}

export default CreateUpdate
